/**
 * Gating for `usePrototyperWorkbenchFacade.runAutoPrototypeSnapshot`.
 *
 * Kept dependency-free so it stays unit-testable in the node test env.
 */

export type AutoSnapshotReason = 'files' | 'build-start' | 'build' | 'stream-end' | 'resume-build';

/**
 * A turn's pre-build and post-build snapshots have byte-identical content — `executeBuild`
 * writes only into the container, never into `workbenchStore.files`/`assets`. Keying the
 * content-fingerprint dedup by turn alone would therefore always suppress the verified
 * upload that follows the unverified one.
 */
export function snapshotDedupKey(llmInteractionId: string, buildVerified: boolean): string {
  return `${llmInteractionId}:${buildVerified ? 'v1' : 'v0'}`;
}

/** Build-phase snapshots run while the stream is still open, so they bypass the busy/cooldown gates. */
export function isForcedSnapshotReason(reason: AutoSnapshotReason): boolean {
  return reason !== 'files';
}

export function shouldDeferForBusy(args: {
  force: boolean;
  isStreaming: boolean;
  clientToolsBusy: boolean;
}): boolean {
  if (args.force) return false;
  return args.isStreaming || args.clientToolsBusy;
}

export function shouldSkipForCooldown(args: {
  reason: AutoSnapshotReason;
  force: boolean;
  sinceFileMs: number;
  sinceBuildStreamMs: number;
  fileCooldownMs: number;
  buildStreamCooldownMs: number;
}): boolean {
  if (args.force) return false;
  if (args.reason === 'files') return args.sinceFileMs < args.fileCooldownMs;
  return args.sinceBuildStreamMs < args.buildStreamCooldownMs;
}
